/**
 * Map bounds utility for tracking and updating map position
 */

export type MapBounds = {
  north: number;
  south: number;
  east: number;
  west: number;
};

/**
 * Serializes map bounds to URL parameters
 */
export function serializeBounds(bounds: MapBounds): string {
  return `${bounds.north},${bounds.south},${bounds.east},${bounds.west}`;
}

/**
 * Deserializes map bounds from URL parameters
 */
export function deserializeBounds(boundsString: string | null): MapBounds | null {
  if (!boundsString) return null;
  
  const parts = boundsString.split(',').map(Number);
  if (parts.length !== 4 || parts.some(isNaN)) return null;
  
  const [north, south, east, west] = parts;
  return { north, south, east, west };
}

/**
 * Checks if a point is within the given bounds
 */
export function isPointInBounds(
  lat: number, 
  lng: number, 
  bounds: MapBounds
): boolean {
  return (
    lat <= bounds.north &&
    lat >= bounds.south &&
    lng <= bounds.east &&
    lng >= bounds.west
  );
}

/**
 * Checks if bounds have changed significantly
 * Returns true if the bounds have changed by more than the threshold percentage
 */
export function haveBoundsChangedSignificantly(
  oldBounds: MapBounds | null,
  newBounds: MapBounds,
  threshold = 0.2 // 20% change threshold
): boolean {
  if (!oldBounds) return true;
  
  // Calculate the area of both bounds
  const oldArea = (oldBounds.north - oldBounds.south) * (oldBounds.east - oldBounds.west);
  const newArea = (newBounds.north - newBounds.south) * (newBounds.east - newBounds.west);
  
  // Calculate the change in area
  const areaChange = Math.abs(newArea - oldArea) / oldArea;
  
  // Check if the center has moved significantly
  const oldCenterLat = (oldBounds.north + oldBounds.south) / 2;
  const oldCenterLng = (oldBounds.east + oldBounds.west) / 2;
  const newCenterLat = (newBounds.north + newBounds.south) / 2;
  const newCenterLng = (newBounds.east + newBounds.west) / 2;
  
  const latChange = Math.abs(newCenterLat - oldCenterLat) / (oldBounds.north - oldBounds.south);
  const lngChange = Math.abs(newCenterLng - oldCenterLng) / (oldBounds.east - oldBounds.west);
  
  return areaChange > threshold || latChange > threshold || lngChange > threshold;
}